// src/components/modal/status-orders/OrderStatusModal.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Clock, MapPin, X, RefreshCw } from "lucide-react";
import { usePedidoStatus } from "../realtime/usePedidoStatus"; // ajuste o path se necessário
import { parseOrderStatus } from "../../../lib/orderStatus";
import { useApi } from "../../../hooks/useApi";

const STATUS_LABEL = {
  0: "Aguardando",
  1: "Preparando",
  2: "Liberado",
  3: "Entregue",
  4: "Finalizado",
};

export default function OrderStatusModal({ isOpen, onClose, order, pedidoId }) {
  const dialogRef = useRef(null);
  const api = useApi();
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Busca o código correto do pedido - usa pedidoId ou localStorage
  const codigoPedido = useMemo(() => {
    if (pedidoId && pedidoId !== "0" && pedidoId !== 0) {
      return String(pedidoId);
    }
    const savedCodigo = localStorage.getItem("pedido.codigo");
    return savedCodigo && savedCodigo !== "0" ? String(savedCodigo) : null;
  }, [pedidoId]);

  // 0..5 vindo da API REST via polling
  const { status } = usePedidoStatus(codigoPedido);

  console.log("status realtime:", status);
  console.log("Pedido realtime:", codigoPedido);

  // NÃO mutar props.order; mantemos o passo atual em estado local
  // Inicializa com o status do hook ou o status do localStorage
  const [currentStep, setCurrentStep] = useState(() => {
    const savedStatus = localStorage.getItem("pedido.status");
    return savedStatus !== null
      ? parseInt(savedStatus, 10)
      : order?.currentStep ?? 0;
  });

  // sempre que o status chegar da API, refletir no passo atual
  useEffect(() => {
    if (Number.isInteger(status) && status >= 0) {
      console.log(
        `🔄 Atualizando currentStep de ${currentStep} para ${status}`
      );
      setCurrentStep(status);
      localStorage.setItem("pedido.status", String(status));

      // Se finalizado ou cancelado (4 ou 5), marca como entregue
      if (status >= 4) {
        localStorage.setItem("pedido.delivery", String(1));
      }
    }
  }, [status]);

  // trava o scroll do body quando abrir
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  // fecha no ESC
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose?.();
    }
    if (isOpen) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  const totalSteps = order?.steps?.length ?? 0;

  const progress = useMemo(() => {
    if (!totalSteps) return 0;
    // Se status >= 4, progresso completo (100%)
    if (currentStep >= 4) return 100;
    const idx = Math.max(0, Math.min(totalSteps - 1, currentStep ?? 0));
    return (idx / (totalSteps - 1)) * 100;
  }, [currentStep, totalSteps]);

  const statusBadge = useMemo(() => {
    // Se status >= 4, sempre mostrar "Concluído"
    if (currentStep >= 4) {
      return { text: "Concluído", cls: "bg-emerald-100 text-emerald-700" };
    }
    const i = Math.max(0, Math.min(totalSteps - 1, currentStep ?? 0));
    if (i === 0)
      return { text: STATUS_LABEL[0], cls: "bg-blue-100 text-blue-700" };
    if (i < totalSteps)
      return { text: "Em andamento", cls: "bg-amber-100 text-amber-700" };
    return { text: "Concluído", cls: "bg-emerald-100 text-emerald-700" };
  }, [currentStep, totalSteps]);

  // Função para atualizar o status do pedido
  const handleRefreshStatus = async () => {
    setIsRefreshing(true);
    try {
      const codigo = codigoPedido;

      if (!codigo || codigo === "0") {
        console.warn("Código do pedido não encontrado");
        return;
      }

      console.log(`🔄 Atualizando status do pedido #${codigo}`);
      const response = await api.get(`/orders/${codigo}/status`);
      const newStatus = parseOrderStatus(response);

      if (newStatus !== null) {
        console.log(`✅ Status atualizado: ${newStatus}`);
        setCurrentStep(newStatus);
        localStorage.setItem("pedido.status", String(newStatus));

        // Se finalizado ou cancelado (4 ou 5), marca como entregue
        if (newStatus >= 4) {
          localStorage.setItem("pedido.delivery", String(1));
        }
      }
    } catch (error) {
      console.error("❌ Erro ao atualizar status:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[9999]"
          aria-labelledby="order-status-title"
          role="dialog"
          aria-modal="true"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Overlay */}
          <motion.div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Card */}
          <motion.div
            ref={dialogRef}
            className="absolute inset-x-0 top-[5%] mx-auto w-full max-w-lg px-4"
            initial={{ y: 20, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 12, opacity: 0, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 220, damping: 22 }}
          >
            <div className="rounded-2xl bg-white shadow-2xl ring-1 ring-black/5">
              {/* Header */}
              <div className="flex items-start justify-between gap-4 p-5 sm:p-6 border-b">
                <div className="space-y-1">
                  <h2
                    id="order-status-title"
                    className="text-xl sm:text-2xl font-bold text-gray-900"
                  >
                    Status do Pedido
                  </h2>
                  <p className="text-sm text-gray-600">
                    Pedido{" "}
                    <span className="font-semibold">
                      #{codigoPedido ?? order?.id ?? "—"}
                    </span>
                    {order?.type ? (
                      <>
                        {" • "}
                        <span className="uppercase tracking-wide text-xs font-semibold text-gray-500">
                          {order.type}
                        </span>
                      </>
                    ) : null}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${statusBadge.cls}`}
                  >
                    {statusBadge.text}
                  </span>
                  <button
                    onClick={onClose}
                    className="rounded-full p-2 hover:bg-gray-100"
                    aria-label="Fechar"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Info extra */}
              {order?.type === "Delivery" && (
                <div className="px-5 sm:px-6 pt-4">
                  <div className="flex flex-wrap items-center gap-3 text-sm text-gray-700">
                    {order?.eta ? (
                      <span className="inline-flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        <span>
                          Previsão: <strong>{order.eta}</strong>
                        </span>
                      </span>
                    ) : null}
                    {order?.address ? (
                      <span className="inline-flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        <span className="truncate" title={order.address}>
                          {order.address}
                        </span>
                      </span>
                    ) : null}
                  </div>
                </div>
              )}

              {/* Progress Bar */}
              <div className="px-5 sm:px-6 pt-4">
                <div className="h-2 w-full rounded-full bg-gray-100">
                  <div
                    className="h-2 rounded-full bg-emerald-500 transition-[width] duration-500"
                    style={{ width: `${progress}%` }}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={Math.round(progress)}
                    role="progressbar"
                  />
                </div>
              </div>

              {/* Steps */}
              <ol className="px-5 sm:px-6 py-5 grid grid-cols-1 gap-4 sm:gap-5">
                {order?.steps?.map((step, idx) => {
                  // Se status >= 4, todos os steps são marcados como concluídos
                  const done = currentStep >= 4 ? true : idx < currentStep;
                  const current =
                    currentStep >= 4 ? false : idx === currentStep;
                  const Icon = () => {
                    if (done)
                      return (
                        <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                      );
                    if (current)
                      return <Clock className="w-5 h-5 text-amber-600" />;
                    return (
                      step.icon ?? <Clock className="w-5 h-5 text-gray-400" />
                    );
                  };
                  return (
                    <li
                      key={step.key}
                      className={`flex items-start gap-3 rounded-xl border p-3.5 sm:p-4 ${
                        done
                          ? "bg-emerald-50/40 border-emerald-200"
                          : current
                          ? "bg-amber-50/40 border-amber-200"
                          : "bg-white border-gray-200"
                      }`}
                    >
                      <div className="mt-0.5">
                        <Icon />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">
                          {step.label}
                        </p>
                        {current && (
                          <p className="text-sm text-gray-600">
                            Estamos trabalhando nisso agora…
                          </p>
                        )}
                        {done && (
                          <p className="text-sm text-emerald-700">Concluído</p>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>

              {/* Ações */}
              <div className="px-5 sm:px-6 pb-5 sm:pb-6 flex flex-col sm:flex-row gap-3 sm:gap-4">
                <button
                  className="flex-1 rounded-xl bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 px-4 py-3 font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleRefreshStatus}
                  disabled={isRefreshing}
                >
                  <RefreshCw
                    className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`}
                  />
                  {isRefreshing ? "Atualizando..." : "Atualizar Status"}
                </button>
                <button
                  className="flex-1 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-3 font-semibold"
                  onClick={onClose}
                >
                  Fechar
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
